/**
 * Short Links Page
 *
 * Mint, retire and download QR codes for arthaus.link links.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Plus, Copy, Check, QrCode, Link2, ExternalLink, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

import { useShortLinks, useCreateShortLink, useSetShortLinkActive } from '../hooks'
import { shortLinksApi } from '../api/easelApi'
import type { ShortLink } from '../types'

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      title="Copy link"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        } catch {
          // Clipboard is blocked in some embedded/insecure contexts. The link is
          // selectable on screen either way, so failing quietly beats an error
          // toast for something the artist can still do by hand.
        }
      }}
    >
      {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
    </Button>
  )
}

function LinkRow({
  link,
  onRetire,
  onRestore,
  onShowQr,
}: {
  link: ShortLink
  onRetire: (link: ShortLink) => void
  onRestore: (link: ShortLink) => void
  onShowQr: (link: ShortLink) => void
}) {
  // A link that exists but has not reached the edge does not resolve yet. Saying
  // so plainly is better than letting the artist discover it by sharing it.
  const pending = link.active && !link.syncedAt

  return (
    <Card className={link.active ? '' : 'opacity-60'}>
      <CardContent className="p-4 flex items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <code className="font-mono text-[15px] font-medium">{link.shortUrl}</code>
            <CopyButton value={link.shortUrl} />
            {!link.active && <Badge variant="outline">Retired</Badge>}
            {pending && <Badge variant="secondary">Not live yet</Badge>}
            {link.source !== 'manual' && <Badge variant="outline">{link.source}</Badge>}
          </div>

          {link.label && <p className="text-sm mt-1">{link.label}</p>}

          <a
            href={link.destinationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-muted-foreground hover:underline break-all inline-flex items-center gap-1 mt-1"
          >
            {link.destinationUrl}
            <ExternalLink className="h-3 w-3 shrink-0" />
          </a>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="icon" title="QR code" onClick={() => onShowQr(link)}>
            <QrCode className="h-4 w-4" />
          </Button>
          {link.active ? (
            <Button variant="ghost" size="sm" onClick={() => onRetire(link)}>
              Retire
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => onRestore(link)}>
              Restore
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

export function ShortLinksPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useShortLinks()
  const createLink = useCreateShortLink()
  const setActive = useSetShortLinkActive()

  const [createOpen, setCreateOpen] = useState(false)
  const [destinationUrl, setDestinationUrl] = useState('')
  const [label, setLabel] = useState('')
  const [code, setCode] = useState('')

  const [qrLink, setQrLink] = useState<ShortLink | null>(null)
  const [retiring, setRetiring] = useState<ShortLink | null>(null)

  const links = data?.data ?? []
  const edgeConfigured = data?.edgeConfigured ?? true

  const submit = async () => {
    await createLink.mutateAsync({
      destinationUrl: destinationUrl.trim(),
      label: label.trim() || undefined,
      code: code.trim() || undefined,
    })
    setDestinationUrl('')
    setLabel('')
    setCode('')
    setCreateOpen(false)
  }

  return (
    <div className="space-y-6 p-8">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-semibold tracking-tight">Short Links</h1>
          <p className="text-muted-foreground">
            Short, shareable arthaus.link URLs for anywhere a long one will not fit
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Link
        </Button>
      </div>

      {!edgeConfigured && (
        <Alert variant="destructive">
          <AlertDescription>
            Links can be saved but will not resolve yet — the link network is not configured for this
            environment. Existing links are unaffected.
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : links.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Link2 className="h-10 w-10 mx-auto text-muted-foreground/50" />
            <h3 className="mt-4 font-medium">No short links yet</h3>
            <p className="text-muted-foreground mt-1 max-w-md mx-auto">
              Turn any long URL into an arthaus.link one. The short link keeps working even if you
              point it somewhere else later — which is what makes it safe to print.
            </p>
            <Button className="mt-6" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create your first link
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {links.map((link) => (
            <LinkRow
              key={link.id}
              link={link}
              onShowQr={setQrLink}
              onRetire={setRetiring}
              onRestore={(l) => setActive.mutate({ id: l.id, active: true })}
            />
          ))}
        </div>
      )}

      {/* Create */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New short link</DialogTitle>
            <DialogDescription>
              Point an arthaus.link URL anywhere. You can change where it goes later without the
              short link changing.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="destinationUrl">Destination</Label>
              <Input
                id="destinationUrl"
                placeholder="https://..."
                value={destinationUrl}
                onChange={(e) => setDestinationUrl(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="label">Label (optional)</Label>
              <Input
                id="label"
                placeholder="What this link is for"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="code">Custom ending (optional)</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground shrink-0">arthaus.link/</span>
                <Input
                  id="code"
                  placeholder="auto-generated"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Leave blank for a short random one. Custom endings cannot be changed afterwards.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!destinationUrl.trim() || createLink.isPending}>
              {createLink.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR */}
      <Dialog open={!!qrLink} onOpenChange={(open) => !open && setQrLink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>QR code</DialogTitle>
            <DialogDescription>
              {qrLink?.shortUrl} — safe to print. It keeps working even if you point the link
              somewhere else later.
            </DialogDescription>
          </DialogHeader>

          {qrLink && (
            <div className="flex flex-col items-center gap-4">
              <img
                src={shortLinksApi.qrUrl(qrLink.id, 512)}
                alt={`QR code for ${qrLink.shortUrl}`}
                className="w-64 h-64 border rounded-md"
              />
              <Button asChild variant="outline">
                <a href={shortLinksApi.qrUrl(qrLink.id, 2048)} download={`arthaus-${qrLink.code}.png`}>
                  Download at print size
                </a>
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Retire */}
      <AlertDialog open={!!retiring} onOpenChange={(open) => !open && setRetiring(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire this link?</AlertDialogTitle>
            <AlertDialogDescription>
              {retiring?.shortUrl} will stop resolving. Anywhere it has already been shared or
              printed will stop working — that cannot be undone for anything on paper. You can
              restore it here at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (retiring) setActive.mutate({ id: retiring.id, active: false })
                setRetiring(null)
              }}
            >
              Retire link
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
